// Wear OS Data Layer Service - Syncs lessons and notes to watch app
// Uses Capacitor WearPlugin to send data to watch app

import { registerPlugin } from '@capacitor/core';

// Get the WearPlugin from Capacitor
let WearPlugin = null;
try {
  WearPlugin = registerPlugin('WearPlugin');
} catch (e) {
  // WearPlugin not available - expected on non-Android platforms
}

const wearService = {
  /**
   * Send lessons and notes to watch app via Wear OS Data Layer
   * @param {Array} lessons - Array of lesson objects with id, summary, start, end, etc.
   * @param {Object} lessonNotes - Map of lessonId -> note text
   */
  sendLessonsToWatch: async (lessons = [], lessonNotes = {}) => {
    lessons = Array.isArray(lessons) ? lessons : [];
    try {
      if (!lessons || lessons.length === 0) {
        console.log('⚠️ wearService: No lessons to send');
        return;
      }

      console.log(`📤 wearService: Sending ${lessons.length} lessons to watch`);

      // Get TODAY's date (not from lesson data)
      const today = new Date();
      const todayDateStr = formatDate(today.toISOString());

      // Only TODAY's lessons go to the watch. The native side arms the "5 minutes left" alarms from whatever
      // it receives and cancels the alarms of every lesson that is NOT in that payload, so pushing another
      // day's lessons (browsing tomorrow, the weekly pre-sync, a watch asking for a different date) used to
      // (a) ring reminders at those lessons' clock times today and (b) wipe today's real reminders.
      // The watch ignores a payload that is not for today anyway.
      lessons = lessons.filter(l => {
        const startIso = l.start?.dateTime || (typeof l.start === 'string' && l.start.includes('T') ? l.start : null);
        return !startIso || formatDate(startIso) === todayDateStr;
      });
      if (lessons.length === 0) {
        console.log('wearService: none of these lessons are for today - not sent to the watch');
        return false;
      }

      // Get the date from the first lesson (all lessons should be same date)
      let lessonDate = '';
      if (lessons.length > 0 && lessons[0].start?.dateTime) {
        lessonDate = formatDate(lessons[0].start.dateTime);
      } else if (lessons.length > 0 && lessons[0].start) {
        // Handle string format like "18:00"
        lessonDate = todayDateStr;
      }

      // Format lessons for watch app
      const formattedLessons = lessons.map(lesson => ({
        id: lesson.id,
        title: lesson.summary || 'Untitled Lesson',
        description: lesson.description || '',
        location: lesson.location || '',
        start: formatTime(lesson.start?.dateTime || lesson.start),
        end: formatTime(lesson.end?.dateTime || lesson.end),
        startDate: formatDate(lesson.start?.dateTime) || todayDateStr,
        note: lessonNotes[lesson.id] || ''
      }));

      // Calculate 5-minute-before-end reminders. Built from END time, not
      // start: filtering by "has this lesson started yet" (the old check)
      // threw out every lesson already in progress, so opening/refreshing
      // the app partway through a lesson never scheduled its reminder at
      // all - and once a lesson stopped being in the future-start set on a
      // later refresh, nothing re-armed or reaffirmed it either. The correct
      // question is simply "is the reminder moment itself still ahead of us"
      // - which the native scheduler (LessonNotificationService / the watch's
      // WatchNotificationService) already checks before arming an alarm.
      const reminders = formattedLessons
        .filter(lesson => lesson.start && lesson.end)
        .map(lesson => {
          const [endHours, endMinutes] = lesson.end.split(':');
          const endTime = new Date();
          endTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
          const sourceLesson = lessons.find(l => l.id === lesson.id);
          const realEnd = sourceLesson && sourceLesson.end && sourceLesson.end.dateTime ? new Date(sourceLesson.end.dateTime) : null;
          if (realEnd && !isNaN(realEnd.getTime())) endTime.setTime(realEnd.getTime()); // the actual end moment, not today + clock time

          // 5 minutes before lesson ends
          const reminderTime = new Date(endTime.getTime() - 5 * 60 * 1000);

          return {
            id: lesson.id,
            title: lesson.title,
            reminderTime: reminderTime.toISOString(),
            message: `${lesson.title} ends in 5 minutes`,
            type: 'lesson-ending'
          };
        });

      // Create data payload with BOTH lessons and reminders
      const payload = {
        date: lessonDate || todayDateStr,
        lessons: formattedLessons,
        reminders: reminders,
        timestamp: new Date().toISOString(),
        count: formattedLessons.length
      };

      // Send via Wear OS Data Layer with reminders included
      const result = await wearService.sendDataToWear('lessons-update', payload);

      return result;
    } catch (error) {
      // Silently fail - watch sync is non-critical
      return false;
    }
  },

  /**
   * Send data to Wear OS devices via Data Layer API
   * @param {String} path - Data layer path (used for logging)
   * @param {Object} data - Data to send
   */
  sendDataToWear: async (path, data) => {
    try {
      if (!WearPlugin) {
        return false;
      }

      // For lessons, use the sendLessonsToWatch method directly
      if (path === 'lessons-update') {
        try {
          const result = await WearPlugin.sendLessonsToWatch({ lessons: JSON.stringify(data) });
          return true;
        } catch (e) {
          return false;
        }
      } else {
        return true;
      }
    } catch (error) {
      return false;
    }
  },

  /**
   * Handle incoming data from watch (called by native code when watch sends data)
   * @param {Object} data - Data from watch
   */
  handleWatchLessonRequest: async (data = {}) => {
    try {

      // Watch can request lesson notes or other data
      if (data.type === 'request-lesson-note') {
        const lessonId = data.lessonId;
        // Return note data - handled by App component
        return { lessonId, status: 'requested' };
      }

      if (data.type === 'add-lesson-note') {
        // Watch trying to add a note
        return { lessonId: data.lessonId, note: data.note, status: 'received' };
      }

      return { status: 'unknown_type' };
    } catch (error) {
      return { status: 'error', error: error.message };
    }
  },

  /**
   * Check watch connectivity status
   */
  checkWatchConnection: async () => {
    try {
      if (!window.cordova || !window.cordova.plugins || !window.cordova.plugins.wearBridge) {
        return { connected: false, reason: 'No wear bridge available' };
      }

      return new Promise((resolve) => {
        window.cordova.plugins.wearBridge.isConnected(
          (connected) => resolve({ connected }),
          () => resolve({ connected: false, reason: 'Bridge check failed' })
        );
      });
    } catch (error) {
      return { connected: false, reason: error.message };
    }
  },

  /**
   * Trigger manual watch sync (call when lessons are updated)
   */
  triggerWatchSync: async (lessons, lessonNotes) => {
    try {
      const result = await wearService.sendLessonsToWatch(lessons, lessonNotes);
      if (result) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      return false;
    }
  },

  /**
   * Sync authentication token to watch app
   * Call this after successful login to enable independent watch fetching
   *
   * Deliberately calls WearPlugin.sendAuthTokenToWatch directly rather than
   * going through sendDataToWear('auth-token', ...): that generic dispatcher
   * only actually implements the 'lessons-update' path (see its own code) -
   * every other path, including 'auth-token', silently did nothing and
   * still returned true, so this always reported success without ever
   * reaching the watch.
   */
  syncAuthToken: async () => {
    try {
      if (!WearPlugin) {
        return false;
      }

      const token = localStorage.getItem('token');

      if (!token) {
        return false;
      }

      await WearPlugin.sendAuthTokenToWatch({ token });
      return true;
    } catch (error) {
      return false;
    }
  },

  /**
   * Check if watch interface is available
   */
  debugWatchInterface: () => {
    if (!WearPlugin) {
      return false;
    }

    return typeof WearPlugin.sendLessonsToWatch === 'function';
  }
};

/**
 * Format time from ISO datetime string to HH:mm format
 */
function formatTime(dateTimeString) {
  if (!dateTimeString) return '';
  try {
    // Handle if already in HH:mm format
    if (dateTimeString.includes(':') && !dateTimeString.includes('T') && !dateTimeString.includes('Z')) {
      return dateTimeString.substring(0, 5); // Return HH:mm
    }

    // Singapore clock time, whatever timezone the phone is set to (the lessons and the server's days are Singapore's)
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return dateTimeString;
    return new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit', hour12: false }).format(date);
  } catch {
    return dateTimeString || '';
  }
}

/**
 * Format date from ISO datetime string to YYYY-MM-DD format
 */
function formatDate(dateTimeString) {
  if (!dateTimeString) return '';
  try {
    // Handle if already in YYYY-MM-DD format
    if (dateTimeString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateTimeString;
    }

    // The Singapore calendar day (en-CA prints YYYY-MM-DD)
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  } catch {
    return '';
  }
}

// Register global handler for watch messages from native code
if (typeof window !== 'undefined') {
  window.handleWatchMessage = (data) => {
    return wearService.handleWatchLessonRequest(data);
  };

  window.triggerWatchSync = (lessons, lessonNotes) => {
    return wearService.triggerWatchSync(lessons, lessonNotes);
  };
}

export default wearService;
